import type { AgentPayout, EarningsLedgerEntry } from '../schemas/agent.js';
import { csvCell } from '../utils/csv.js';

/**
 * Pure CSV exporters for the agent + recruiter earnings / payout
 * statements (the "Export CSV" buttons on `/earnings`, `/recruiter/earnings`,
 * `/payouts`, `/recruiter/payouts`).
 *
 * MONEY ACCURACY: amounts are integer sen. The ONLY divide-by-100 is
 * `senToRm`, applied once per amount cell at this render boundary (mirrors
 * `renderDuitNowCsv`). Signs are preserved so a clawback shows as e.g.
 * "-50.00". Empty input yields a header-only file — never a fake zero row.
 *
 * Column headers accept optional localised `labels` (resolved from i18n by
 * the caller); English defaults keep the functions pure + trivially
 * testable and are what the unit tests assert against.
 */

const CRLF = '\r\n';

/** Integer sen → RM string, exactly two decimals, sign preserved. */
function senToRm(sen: number): string {
  return (sen / 100).toFixed(2);
}

/** Duck-typed Firestore-Timestamp → epoch ms (0 when unrecognised). */
function tsMs(ts: unknown): number {
  if (!ts || typeof ts !== 'object') return 0;
  const obj = ts as { toMillis?: () => number; seconds?: number; nanoseconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') {
    return obj.seconds * 1000 + Math.floor((obj.nanoseconds ?? 0) / 1_000_000);
  }
  return 0;
}

/** ISO-8601 UTC string for a timestamp, or '' when absent — deterministic. */
function isoOrEmpty(ms: number): string {
  return ms > 0 ? new Date(ms).toISOString() : '';
}

// ── earnings statement ──────────────────────────────────────────────────

export interface EarningsCsvLabels {
  readonly date: string;
  readonly kind: string;
  readonly operator: string;
  readonly invoice: string;
  readonly rate: string;
  readonly multiplier: string;
  readonly amount: string;
  readonly payout: string;
}

export const DEFAULT_EARNINGS_CSV_LABELS: EarningsCsvLabels = {
  date: 'Date',
  kind: 'Kind',
  operator: 'Operator',
  invoice: 'Invoice',
  rate: 'Rate',
  multiplier: 'Multiplier',
  amount: 'Amount (RM)',
  payout: 'Payout ID',
};

/**
 * One row per ledger entry: date, kind, operator (business name snapshot
 * from `basis.tenantBusinessName`, falling back to the tenant id), invoice,
 * rate, multiplier, signed amount in RM, and the payout id (present once the
 * entry has been swept into a payout).
 */
export function toEarningsStatementCsv(
  entries: readonly EarningsLedgerEntry[],
  labels: EarningsCsvLabels = DEFAULT_EARNINGS_CSV_LABELS,
): string {
  const header = [
    labels.date,
    labels.kind,
    labels.operator,
    labels.invoice,
    labels.rate,
    labels.multiplier,
    labels.amount,
    labels.payout,
  ]
    .map(csvCell)
    .join(',');

  const rows = entries.map((e) => {
    const basis = e.basis as Record<string, unknown>;
    const businessName = basis['tenantBusinessName'];
    const operator =
      typeof businessName === 'string' && businessName.length > 0 ? businessName : e.tenantId;
    return [
      isoOrEmpty(tsMs(e.accruedAt)),
      e.kind,
      csvCell(operator),
      csvCell(e.invoiceId ?? ''),
      String(e.rate),
      String(e.multiplier),
      senToRm(e.amount),
      csvCell(e.payoutId ?? ''),
    ].join(',');
  });

  return [header, ...rows].join(CRLF) + CRLF;
}

// ── payout statement ────────────────────────────────────────────────────

export interface PayoutCsvLabels {
  readonly payoutId: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly status: string;
  readonly method: string;
  readonly gross: string;
  readonly items: string;
  readonly initiated: string;
  readonly paid: string;
  readonly txRef: string;
}

export const DEFAULT_PAYOUT_CSV_LABELS: PayoutCsvLabels = {
  payoutId: 'Payout ID',
  periodStart: 'Period start',
  periodEnd: 'Period end',
  status: 'Status',
  method: 'Method',
  gross: 'Gross (RM)',
  items: 'Entries',
  initiated: 'Initiated',
  paid: 'Paid',
  txRef: 'Bank ref',
};

/**
 * One row per payout batch: id, period bounds, status, method, gross in RM,
 * item count, initiated + paid timestamps, and the bank/DuitNow reference.
 */
export function toPayoutStatementCsv(
  payouts: readonly AgentPayout[],
  labels: PayoutCsvLabels = DEFAULT_PAYOUT_CSV_LABELS,
): string {
  const header = [
    labels.payoutId,
    labels.periodStart,
    labels.periodEnd,
    labels.status,
    labels.method,
    labels.gross,
    labels.items,
    labels.initiated,
    labels.paid,
    labels.txRef,
  ]
    .map(csvCell)
    .join(',');

  const rows = payouts.map((p) =>
    [
      csvCell(p.id),
      isoOrEmpty(tsMs(p.periodStart)),
      isoOrEmpty(tsMs(p.periodEnd)),
      p.status,
      p.method,
      senToRm(p.grossAmount),
      String(p.items.length),
      isoOrEmpty(tsMs(p.initiatedAt)),
      isoOrEmpty(tsMs(p.paidAt)),
      csvCell(p.txRef ?? ''),
    ].join(','),
  );

  return [header, ...rows].join(CRLF) + CRLF;
}
